import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { UNITS_OF_MEASURE } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";

const NewStockItem = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [unitOfMeasure, setUnitOfMeasure] = useState("piece");
  const [currentStock, setCurrentStock] = useState(0);
  const [minThreshold, setMinThreshold] = useState(0);
  const [maxThreshold, setMaxThreshold] = useState(0);
  const [reorderPoint, setReorderPoint] = useState(0);
  const [unitCost, setUnitCost] = useState(0);
  const [location, setLocation] = useState("");
  const [isSerialized, setIsSerialized] = useState(false);
  const [isReturnable, setIsReturnable] = useState(true);
  const [itemCode, setItemCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetch("/api/getAllInventoryCategories", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setCategories(d.categories || [])).catch(console.error);
  }, []);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const res = await fetch("/api/addInventoryCategory", {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newCategoryName }),
      });
      const data = await res.json();
      if (res.ok) {
        setCategories([...categories, data.category]);
        setCategory(data.category._id);
        setNewCategoryName(""); setShowCategoryModal(false);
      }
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    if (!name || !unitOfMeasure || unitCost === undefined) {
      setErrorMessage("Name, unit, and unit cost are required");
      setTimeout(() => setErrorMessage(""), 3000); return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/addInventoryItem", {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name, description, category: category || undefined, unitOfMeasure,
          currentStock: Number(currentStock), minThreshold: Number(minThreshold),
          maxThreshold: Number(maxThreshold), reorderPoint: Number(reorderPoint),
          unitCost: Number(unitCost), location, isSerialized, isReturnable, barcode,
          itemCode: itemCode || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccessMessage("Item created successfully");
        setTimeout(() => navigate("/StockMaster"), 1500);
      } else { setErrorMessage(data.error || "Failed to create item"); setTimeout(() => setErrorMessage(""), 3000); }
    } catch (err) { setErrorMessage("Failed to create item"); setTimeout(() => setErrorMessage(""), 3000);
    } finally { setLoading(false); }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/StockMaster")} className="p-2 rounded-full hover:bg-gray-100"><IoArrowBack className="text-xl text-primary" /></button>
        <h2 className="text-xl font-semibold text-gray-800">New Stock Item</h2>
      </div>
      {errorMessage && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{errorMessage}</div>}
      {successMessage && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">{successMessage}</div>}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">Item Name <span className="text-red-500">*</span></label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., A4 Paper" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Item Code</label>
            <input type="text" value={itemCode} onChange={(e) => setItemCode(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., PAPER-A4-001" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Category</label>
            <div className="flex gap-2">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select Category</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <button onClick={() => setShowCategoryModal(true)} className="px-3 py-2 border border-primary text-primary rounded-lg text-sm hover:bg-blue-50">+</button>
            </div>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Unit of Measure <span className="text-red-500">*</span></label>
            <select value={unitOfMeasure} onChange={(e) => setUnitOfMeasure(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              {UNITS_OF_MEASURE.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Unit Cost (Nu) <span className="text-red-500">*</span></label>
            <input type="number" min="0" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Initial Stock</label>
            <input type="number" min="0" value={currentStock} onChange={(e) => setCurrentStock(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Barcode (Optional)</label>
            <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Location / Shelf</label>
            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Warehouse A, Shelf 3" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Thresholds & Alerts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">Min Threshold</label>
            <input type="number" min="0" value={minThreshold} onChange={(e) => setMinThreshold(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Max Threshold</label>
            <input type="number" min="0" value={maxThreshold} onChange={(e) => setMaxThreshold(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block font-medium mb-2 text-blue-500">Reorder Point</label>
            <input type="number" min="0" value={reorderPoint} onChange={(e) => setReorderPoint(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Item Properties</h3>
        <div className="flex gap-6 mb-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isSerialized} onChange={(e) => setIsSerialized(e.target.checked)} className="rounded" /> Serialized Asset (e.g., Laptop)</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={isReturnable} onChange={(e) => setIsReturnable(e.target.checked)} className="rounded" /> Returnable</label>
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={() => navigate("/StockMaster")} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50">{loading ? "Creating..." : "Create Item"}</button>
        </div>
      </div>

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-4">New Category</h3>
            <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4" placeholder="Category name" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCategoryModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
              <button onClick={handleAddCategory} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewStockItem;
